import { useCallback, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { consumeManagerReturnTo, consumeManagerSessionNotice } from "@/lib/authSession";
import { TS_SESSION_CHANGED_EVENT } from "@/lib/tsIdentity";
import { withOpsBase } from "@/lib/opsBase";
import TsSessionGate from "@/components/mobile/TsSessionGate";
import Login from "@/pages/Login";

/**
 * Manage front door — phone + OTP, the same identity as the Team hub.
 *
 * Ops authenticates directly off the TS session now — no manager-token
 * exchange. `onReady` only has to make sure AuthContext has noticed the
 * session (it already listens for `TS_SESSION_CHANGED_EVENT`, but that event
 * fires from within the gate's own resume flow, before this component's
 * listener is guaranteed to be attached) and send the visitor on to wherever
 * they were headed before being asked to sign in.
 *
 * The password form is kept behind ?password=1 rather than deleted. It is the
 * documented rollback if Firebase OTP is unavailable, and removing it before
 * the phone path has proven itself in daily use would leave ops with no way in.
 */
export default function ManageLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sessionNotice] = useState(() => consumeManagerSessionNotice());

  const usePassword = searchParams.get("password") === "1";

  /** TS session established — it is Ops's credential as-is; just sync and go. */
  const handleReady = useCallback(() => {
    window.dispatchEvent(new Event(TS_SESSION_CHANGED_EVENT));
    navigate(consumeManagerReturnTo(), { replace: true });
  }, [navigate]);

  if (usePassword) return <Login />;

  return (
    <div className="min-h-screen bg-[#F5F2EA]">
      {sessionNotice ? (
        <div className="mx-auto max-w-md px-4 pt-4">
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-center text-sm text-amber-800">
            {sessionNotice}
          </div>
        </div>
      ) : null}
      <TsSessionGate onReady={handleReady} />
      <div className="pb-8 text-center">
        <a
          href={`${withOpsBase("/login")}?password=1`}
          className="text-xs font-semibold text-[#2D2D2D]/45 underline"
        >
          Sign in with the shared password instead
        </a>
      </div>
    </div>
  );
}
